import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { scrollToSection } from "@/components/landing/Nav";
import { api } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const DEFAULTS = {
  hero_quote: "Unlocking Potential.\nUnleashing Success.",
  hero_title_1: "AB Hunter",
  hero_title_2: "Consulting USA",
  hero_subcopy: "A full-service strategic consulting practice — improving individual performance, reforming school systems, and creating long-term shifts in the community.",
  bet_strip: "Building Bridges, Elevating Communities and Transforming Leaders (B.E.T)",
};

function MaskedLine({ children, delay, className = "" }) {
  return (
    <span className={`block overflow-hidden pb-2 ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { scrollYProgress } = useScroll();
  const imgY = useTransform(scrollYProgress, [0, 0.25], [0, -70]);
  const fade = useTransform(scrollYProgress, [0, 0.18], [1, 0]);
  const [c, setC] = useState(DEFAULTS);

  useEffect(() => {
    api.get("/content")
      .then(({ data }) => setC({ ...DEFAULTS, ...data }))
      .catch(() => {});
  }, []);

  const strip = c.bet_strip;
  const betIdx = strip.indexOf("(B.E.T)");

  return (
    <section id="home" data-testid="hero-section" className="grain relative flex min-h-screen flex-col overflow-hidden bg-ink">
      {/* diagonal line texture, like the original */}
      <div className="diag-lines pointer-events-none absolute inset-0" aria-hidden />

      <div className="grid flex-1 grid-cols-1 lg:grid-cols-2">
        {/* Left: quote + title */}
        <motion.div style={{ opacity: fade }} className="relative flex flex-col justify-between px-6 pb-10 pt-28 sm:px-12 lg:min-h-screen lg:py-14 lg:pl-14 lg:pr-10">
          <motion.p
            data-testid="hero-quote"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
            className="max-w-sm font-display text-xl font-medium italic leading-snug text-foreground/90 sm:text-2xl"
          >
            {c.hero_quote.split("\n").map((line, i) => (
              <span key={i}>"{line}"{i < c.hero_quote.split("\n").length - 1 && <br />}</span>
            ))}
          </motion.p>

          <div className="my-12 lg:my-0">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-gold" />
            </div>
            <h1 className="font-display font-semibold italic leading-[0.98] tracking-tight text-gold">
              <MaskedLine delay={0.45} className="text-5xl sm:text-6xl lg:text-7xl xl:text-[5.2rem]">{c.hero_title_1}</MaskedLine>
              <MaskedLine delay={0.6} className="text-4xl sm:text-5xl lg:text-6xl xl:text-[4rem]">{c.hero_title_2}</MaskedLine>
            </h1>
            <motion.p
              data-testid="hero-subcopy"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
              className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground"
            >
              {c.hero_subcopy}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Button
                data-testid="hero-explore-events-button"
                size="lg"
                onClick={() => scrollToSection("events")}
                className="rounded-full bg-gold px-7 font-semibold text-ink transition-all duration-300 hover:scale-[1.04] hover:bg-gold-hover hover:shadow-[0_0_36px_rgba(212,175,55,0.35)]"
              >
                Explore Events <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                data-testid="hero-book-call-button"
                size="lg"
                variant="outline"
                onClick={() => scrollToSection("contact")}
                className="rounded-full border-gold/40 bg-transparent px-7 text-foreground transition-all duration-300 hover:border-gold hover:bg-gold/10"
              >
                Book a Call
              </Button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.6 }}
            className="hidden items-center gap-2 text-muted-foreground lg:flex"
          >
            <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
              <ArrowDown className="h-4 w-4 text-gold" />
            </motion.span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll to explore</span>
          </motion.div>
        </motion.div>

        {/* Right: the original B&W portrait, full-bleed */}
        <motion.div
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.25, ease: EASE }}
          className="relative h-[58vh] overflow-hidden lg:h-auto lg:min-h-screen"
        >
          <motion.img
            src="/assets/hero-portrait.jpg"
            alt="Dr. Antiqua Hunter, founder of AB Hunter Consulting USA"
            style={{ y: imgY }}
            className="h-[110%] w-full object-cover object-top grayscale"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ink via-ink/20 to-transparent lg:via-transparent" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-ink/30" />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.4, ease: EASE }}
            className="absolute bottom-6 right-6 hidden rounded-2xl border border-gold/30 bg-ink/85 px-5 py-4 backdrop-blur sm:block"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Founder & Principal Consultant</p>
            <p className="mt-1 font-display text-lg font-semibold text-white">Dr. Antiqua Hunter</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom B.E.T strip, echoing the original */}
      <motion.div
        data-testid="hero-bet-strip"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        className="relative border-t border-gold/15 bg-ink px-6 py-5 text-center"
      >
        <p className="text-sm font-semibold tracking-wide text-foreground sm:text-base">
          {betIdx === -1 ? strip : <>{strip.slice(0, betIdx)}<span className="text-gold">(B.E.T)</span>{strip.slice(betIdx + 7)}</>}
        </p>
      </motion.div>
    </section>
  );
}
